import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { deepLink, siteFolderName } from '@erp-attach/shared';
import { useApiQuery } from '../api/useApiQuery';
import { ApiRequestError } from '../api/client';
import { config } from '../config';
import { addRecent, notifyRecentChange } from '../lib/recent';
import { routes } from '../lib/links';
import { EntityHeader } from '../components/EntityHeader';
import { FolderWorkspace } from '../components/FolderWorkspace';
import { ProvisioningState } from '../components/ProvisioningState';
import { ArchiveJobButton } from '../components/ArchiveJobButton';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { FullPageSpinner } from '../components/ui/Spinner';
import { NotFoundPage } from './NotFoundPage';

export function JobPage() {
  const { siteId = '' } = useParams();
  const query = useApiQuery((api, signal) => api.jobs.get(siteId, signal), [siteId]);
  const data = query.data;

  useEffect(() => {
    if (data?.status === 'ready') {
      addRecent({
        kind: 'site',
        number: data.entity.partySiteNumber ?? data.entity.siteId,
        label: siteFolderName(data.entity.jobReference, data.entity.partySiteNumber, data.entity.siteId),
        href: routes.job(data.entity.siteId),
      });
      notifyRecentChange();
    }
  }, [data]);

  if (query.loading) return <FullPageSpinner label="Loading job" />;
  if (query.error) {
    if (query.error instanceof ApiRequestError && query.error.isNotFound) return <NotFoundPage what="job" />;
    return <ErrorBanner title="Job could not be loaded" error={query.error} />;
  }
  if (!data) return null;
  if (data.status === 'provisioning') {
    return <ProvisioningState kind="site" number={data.entity.number || siteId} name={data.entity.name} onRetry={query.reload} />;
  }

  const { entity, folders } = data;
  const name = siteFolderName(entity.jobReference, entity.partySiteNumber, entity.siteId);
  const root = folders[0];
  const archived = entity.archivedAt !== null || root?.status === 'archived';

  return (
    <div className="gsl-record">
      <EntityHeader
        kind="site"
        crumbs={[{ label: 'Customers', href: routes.browseCustomers }, { label: entity.customer.name, href: routes.customer(entity.customer.accountNumber) }]}
        name={name}
        number={entity.partySiteNumber ?? entity.siteId}
        branchCode={entity.customer.branchCode}
        status={archived ? 'archived' : entity.status}
        subtitle={
          <>
            {entity.address ? <span>{entity.address}. </span> : null}
            <Link className="gsl-link" to={routes.customer(entity.customer.accountNumber)}>
              {entity.customer.name} ({entity.customer.accountNumber})
            </Link>
          </>
        }
        deepLink={deepLink.site(entity.siteId, config.appBaseUrl)}
        actions={
          <ArchiveJobButton
            siteId={entity.siteId}
            jobReference={name}
            archived={archived}
            canArchive={root?.actions.includes('archive') ?? false}
            onDone={query.reload}
          />
        }
      />
      <FolderWorkspace entityType="site" folders={folders} />
    </div>
  );
}
